import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { Alert, Button, Collapse, Radio, Tag } from 'antd';
import { Logotipo } from '@titularis/ui';
import { useMovimentoReduzido } from './movimento';
import questao from '../conteudo/demo-enac.json';

const DURACAO = 12000;
const ETAPAS = [
  { nome: 'Resolva', detalhe: 'Uma questão real do Enac' },
  { nome: 'Entenda', detalhe: 'Comentários por alternativa' },
];

/** Amostra pública estática autorizada: somente a questão 1 do Enac 2025.1. */
export function Demonstracao() {
  const reduzido = useMovimentoReduzido();
  const [etapa, setEtapa] = useState(0);
  const [resposta, setResposta] = useState<string>();
  const [reproduzindo, setReproduzindo] = useState(true);
  const [visivel, setVisivel] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState(() => !document.hidden);
  const moldura = useRef<HTMLDivElement>(null);
  const leitura = useRef<HTMLDivElement>(null);
  const automatico = reproduzindo && !reduzido;
  const animando = automatico && visivel && abaAtiva;

  useEffect(() => {
    const elemento = moldura.current;
    if (!elemento || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entrada]) => setVisivel(entrada?.isIntersecting ?? false),
      { threshold: 0.2 },
    );
    observer.observe(elemento);
    const aoMudar = () => setAbaAtiva(!document.hidden);
    document.addEventListener('visibilitychange', aoMudar);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', aoMudar);
    };
  }, []);

  useEffect(() => {
    leitura.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [etapa]);

  useEffect(() => {
    if (!animando) return;
    const selecao =
      etapa === 0
        ? window.setTimeout(() => setResposta((atual) => atual ?? questao.gabarito), 6000)
        : undefined;
    const timer = window.setTimeout(() => {
      const proxima = (etapa + 1) % ETAPAS.length;
      setEtapa(proxima);
      if (proxima === 0) setResposta(undefined);
    }, DURACAO);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(selecao);
    };
  }, [animando, etapa]);

  function selecionar(indice: number) {
    setEtapa(indice);
    setReproduzindo(false);
  }

  return (
    <div
      id="demonstracao"
      className="demonstracao"
      ref={moldura}
      style={{ '--demo-duracao': `${DURACAO}ms` } as CSSProperties}
      role="region"
      aria-label="Demonstração interativa da Titularis"
    >
      <div className="dispositivo">
        <div className="dispositivo-tela">
          <div className="produto-janela">
            <span className="produto-endereco">app.titularis.com.br</span>
            <span className="produto-exemplo">Demonstração interativa</span>
          </div>
          <div className="produto-layout">
            <aside className="produto-sidebar" aria-label="Navegação da demonstração">
              <Logotipo altura={23} />
              <span className="produto-home">
                <HomeOutlined /> Meu estudo
              </span>
              <span className="produto-grupo">CARTÓRIOS</span>
              <button type="button" className="ativo" onClick={() => selecionar(0)}>
                <ReadOutlined /> Enac
              </button>
              <div className="produto-sidebar-rodape">
                <span className="produto-avatar">T</span>
                <div>
                  Seu espaço de estudo<small>Enac · Questões comentadas</small>
                </div>
              </div>
            </aside>
            <div className="produto-area">
              <div className="produto-barra">
                <span>
                  Cartórios <span aria-hidden="true">/</span> <strong>Enac</strong>
                </span>
                <Tag variant="filled">FGV · 2025.1</Tag>
              </div>
              <div
                ref={leitura}
                className="produto-leitura"
                tabIndex={0}
                aria-label="Conteúdo da questão, role para ler"
                onWheel={() => setReproduzindo(false)}
                onTouchStart={() => setReproduzindo(false)}
                onFocusCapture={() => setReproduzindo(false)}
              >
                <div className="produto-conteudo">
                  <div className="produto-titulo">
                    <div>
                      <span className="sobretitulo">DIREITO NOTARIAL E REGISTRAL</span>
                      <h2>
                        {['Questão 1 · Protesto de títulos', 'Comentários das alternativas'][etapa]}
                      </h2>
                    </div>
                    <span className="produto-indice">
                      01<span> / 100</span>
                    </span>
                  </div>
                  <div
                    id="demo-cena"
                    className="produto-cartao"
                    aria-live={automatico ? 'off' : 'polite'}
                  >
                    {etapa === 0 ? (
                      <div className="produto-cena" key="questao">
                        <div className="produto-cartao-topo">
                          <Tag>ENAC 2025.1</Tag>
                          <span>FGV · Caderno tipo 1</span>
                        </div>
                        <div
                          className="demo-texto demo-enunciado"
                          dangerouslySetInnerHTML={{ __html: questao.enunciado }}
                        />
                        <Radio.Group
                          className="produto-alternativas"
                          value={resposta}
                          aria-label="Escolha uma alternativa"
                          onChange={(e) => {
                            setResposta(e.target.value);
                            setReproduzindo(false);
                          }}
                        >
                          {questao.alternativas.map((alternativa) => (
                            <Radio key={alternativa.letra} value={alternativa.letra}>
                              <strong>{alternativa.letra}</strong>
                              <span dangerouslySetInnerHTML={{ __html: alternativa.html }} />
                            </Radio>
                          ))}
                        </Radio.Group>
                      </div>
                    ) : (
                      <div className="produto-cena" key="comentario">
                        <div className="produto-cartao-topo">
                          <Tag color="success">
                            <CheckCircleOutlined /> Gabarito C
                          </Tag>
                          <span>Comentário Titularis</span>
                        </div>
                        {resposta && resposta !== questao.gabarito ? (
                          <Alert
                            type="info"
                            showIcon
                            title={`Você marcou ${resposta}. Confira por que o gabarito é C.`}
                          />
                        ) : null}
                        <h3>Renegociação de dívidas protestadas</h3>
                        <Collapse
                          defaultActiveKey={[resposta ?? questao.gabarito]}
                          accordion
                          ghost
                          onChange={() => setReproduzindo(false)}
                          items={questao.alternativas.map((alternativa) => ({
                            key: alternativa.letra,
                            label: `${alternativa.letra} · ${alternativa.letra === questao.gabarito ? 'Correta' : 'Incorreta'}`,
                            children: (
                              <div
                                className="demo-texto demo-comentario-protegido"
                                onCopy={(evento) => evento.preventDefault()}
                                dangerouslySetInnerHTML={{ __html: alternativa.comentario }}
                              />
                            ),
                          }))}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="produto-rodape-app">
                <span>Questão 1 de 100</span>
                {etapa === 0 ? (
                  <Button type="primary" disabled={!resposta} onClick={() => selecionar(1)}>
                    Ver comentário <ArrowRightOutlined />
                  </Button>
                ) : (
                  <Button onClick={() => selecionar(0)}>Voltar à questão</Button>
                )}
              </div>
            </div>
          </div>
          <div className="dispositivo-home" aria-hidden="true" />
        </div>
        <picture className="dispositivo-fotografia" aria-hidden="true">
          <source
            media="(min-width: 768px)"
            srcSet="/images/devices/macbook-air.png"
            width={1513}
            height={1039}
          />
          <img
            src="/images/devices/iphone-17.png"
            width={973}
            height={1616}
            alt=""
            decoding="async"
            draggable={false}
          />
        </picture>
      </div>
      <div className="demo-controles">
        <div className="demo-etapas" role="group" aria-label="Etapas da demonstração">
          {ETAPAS.map((item, indice) => (
            <button
              type="button"
              key={item.nome}
              className="demo-etapa"
              aria-pressed={etapa === indice}
              aria-controls="demo-cena"
              onClick={() => selecionar(indice)}
            >
              <span className="demo-numero">0{indice + 1}</span>
              <span>
                <strong>{item.nome}</strong>
                <small>{item.detalhe}</small>
              </span>
              {etapa === indice && animando ? (
                <span key={`${etapa}-progresso`} className="demo-progresso" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </div>
        {!reduzido ? (
          <Button
            type="text"
            className="demo-play"
            icon={automatico ? <PauseOutlined /> : <PlayCircleOutlined />}
            aria-label={automatico ? 'Pausar demonstração' : 'Reproduzir demonstração'}
            onClick={() => setReproduzindo((atual) => !atual)}
          />
        ) : null}
      </div>
    </div>
  );
}
